import React, { useState, useContext, useEffect } from 'react';
import { Routes, Route, useNavigate } from 'react-router-dom';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { UserManagement } from '@/components/admin/UserManagement';
import { BusinessManager } from '@/components/admin/BusinessManager';
import { PlatformFeaturesManager } from '@/components/admin/PlatformFeaturesManager';
import CompanyManager from '@/components/admin/CompanyManager';
import { FileUploadTest } from '@/components/admin/FileUploadTest';
import ChatConfig from '@/components/admin/ChatConfig';
import { Skeleton } from '@/components/ui/skeleton';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';
import { ExternalLink, LogOut, Settings } from 'lucide-react';
import { Logo } from '@/components/Logo';
import AdminSettings from '@/pages/AdminSettings';

const AdminPage = () => {
  // 1. State hooks
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('companies');
  const navigate = useNavigate();
  
  // 2. Context hooks - keep all context hooks together
  const { user, signOut } = useAuth();

  // 3. Effect hooks
  useEffect(() => {
    // Simulate loading to ensure components are properly mounted
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 1000);

    return () => clearTimeout(timer);
  }, []);

  if (isLoading) {
    return (
      <div className="container mx-auto py-8 space-y-8 bg-white">
        <Skeleton className="h-10 w-64 mb-8" />
        <Skeleton className="h-8 w-full max-w-md mb-6" />
        <div className="space-y-6">
          <Skeleton className="h-[400px] w-full rounded-lg" />
        </div>
      </div>
    );
  }

  const handleLogout = async () => {
    await signOut();
  };

  return (
    <Routes>
      <Route path="settings" element={<AdminSettings />} />
      <Route path="*" element={
        <div className="container mx-auto pt-12 pb-8 px-8 space-y-8 bg-background">
          <div className="flex flex-col space-y-6 mb-8">
            <div className="flex items-center gap-4 mb-2">
              <Logo className="h-12 w-auto" />
              <h1 className="text-3xl font-bold">Admin Dashboard</h1>
            </div>
            
            <div className="flex justify-between items-center">
              <p className="text-muted-foreground">Manage your platform settings and content</p>
              <div className="flex space-x-4">
                <Button variant="outline" onClick={() => navigate('/admin/settings')} className="flex items-center gap-2">
                  <Settings size={18} />
                  Settings
                </Button>
                <Button variant="outline" asChild>
                  <Link to="/" className="flex items-center gap-2">
                    <ExternalLink size={18} />
                    View Website
                  </Link>
                </Button>
                <Button variant="destructive" onClick={handleLogout} className="flex items-center gap-2">
                  <LogOut size={18} />
                  Logout
                </Button>
              </div>
            </div>
          </div>
          
          <Tabs defaultValue="companies" className="space-y-4">
            <TabsList>
              <TabsTrigger value="companies">Companies</TabsTrigger>
              <TabsTrigger value="users">Users</TabsTrigger>
              <TabsTrigger value="businesses">Businesses</TabsTrigger>
              <TabsTrigger value="features">Platform Features</TabsTrigger>
              <TabsTrigger value="chat">Chat Settings</TabsTrigger>
              <TabsTrigger value="uploads">File Uploads</TabsTrigger>
            </TabsList>

            <TabsContent value="companies" className="space-y-4">
              <CompanyManager />
            </TabsContent>

            <TabsContent value="users" className="space-y-4">
              <h2 className="text-2xl font-bold">User Management</h2>
              <UserManagement />
            </TabsContent>

            <TabsContent value="businesses" className="space-y-4">
              <h2 className="text-2xl font-bold">Business Management</h2>
              <BusinessManager />
            </TabsContent>

            <TabsContent value="features" className="space-y-4">
              <h2 className="text-2xl font-bold">Platform Features</h2>
              <PlatformFeaturesManager />
            </TabsContent>

            <TabsContent value="chat" className="space-y-4">
              <h2 className="text-2xl font-bold">Chat Settings</h2>
              <ChatConfig />
            </TabsContent>

            <TabsContent value="uploads" className="space-y-4">
              <h2 className="text-2xl font-bold">File Uploads</h2>
              <FileUploadTest />
            </TabsContent>
          </Tabs>
        </div>
      } />
    </Routes>
  );
};

export default AdminPage;
